import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BackendSystemService } from '@app/portal/services/backend-system.service';
import { ToastService } from '@app/portal/services/toast-service';
import { ErrorToastService } from '@app/shared/toasts/error-toast-service';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class HydraSystemHttpInterceptor implements HttpInterceptor {
  private logger = loggerFor('HydraSystemHttpInterceptor');

  private targetSystemOnApplicationLoad: string;
  private switchInProgress: boolean = false;

  constructor(
    private errorToastService: ErrorToastService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private backendSystemService: BackendSystemService
  ) {
    this.targetSystemOnApplicationLoad = this.backendSystemService.getHydraTargetSystemFromCookie();
    this.logger.debug('target system on application load', this.targetSystemOnApplicationLoad);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // this.logger.debug(req, next)

    // if the switch is already in progress then we do not want to send any additional requests over the network
    if (this.switchInProgress) {
      this.logger.warn('system switch is already in progress', req);
      // returning observable that never emits a value
      return new Subject<HttpEvent<any>>();
    }

    return next.handle(req).pipe(
      map((resp: HttpEvent<any>) => {
        // check that the event is of the right type, otherwise the code is executed also for not initialized resp
        if (resp instanceof HttpResponse) {
          const currentTargetSystem = this.backendSystemService.getHydraTargetSystemFromCookie();

          if (this.switchInProgress === false && currentTargetSystem !== this.targetSystemOnApplicationLoad) {
            this.switchInProgress = true;
            this.logger.trace(
              'Reloading: Target System chagend from',
              this.targetSystemOnApplicationLoad,
              'to',
              currentTargetSystem,
              req
            );

            // disable danger toasters so errors for bad requests are not shown (that were already issued and that went to the wrong target system)
            this.toastService.disable();
            // clear all shown toasters (potentially those showing requests gone bad)
            this.toastService.clear();
            this.errorToastService.clear();
            this.errorToastService.disable();
            // show info to user that the application will be reloaded
            this.toastService.show({
              body: this.translateService.instant('portal.backendSystem.switching.text'),
              delay: 0,
              header: this.translateService.instant('portal.backendSystem.switching.title'),
              type: 'info',
            });

            const waitUntilPromiseResolves =
              this.backendSystemService.locationChangeInProgressP || Promise.resolve(null);
            waitUntilPromiseResolves.finally(() => {
              // reload the application in one second
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            });
          }

          // just for diagnosing which request is processed after `switchInProgress=true`
          if (this.switchInProgress) {
            this.logger.trace('switch is in progress after response', req, resp);
          }
        }

        return resp;
      })
    );
  }
}
